// src/templates/AlbumTemplate.jsx
import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Heart, 
  Download, 
  Share2, 
  ZoomIn, 
  Play, 
  Mic,
  Grid, 
  List,
  Filter,
  Search,
  Upload,
  ExternalLink,
  ArrowLeft,
  PanelLeft,
  PanelRight,
  Plus,
  BookOpen,
  Eye,
  Settings,  
  Shield, 
  LogIn,
  Image as ImageIcon,
  MessageCircle,
  Bookmark,
  MoreHorizontal,
  X,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  Users,
  Lock,
  Globe
} from 'lucide-react';
import Masonry, { ResponsiveMasonry } from 'react-responsive-masonry';
import { useAuth } from '../../contexts/AuthContext';
import { albumService } from '../../services/albumService';
import { MediaLightbox } from '../../components/album/AlbumMediaModal';
import { MediaInteractions } from '../../components/album/AlbumInteractions';
import { GallerySelector } from '../../components/album/GallerySelector';
import { ShareModal } from '../books/modals/ShareModal';
import { MediaCard } from '../../components/album/AlbumMediaCard';
import { MediaListItem } from '../../components/album/MediaListItem';
import { LayoutRenderer } from '../renderers/LayoutRenderer';
import AlbumStoriesManager from '../album/AlbumStoriesManager';
import StoryCard from '../../components/album/StoryCard';
import StoryPlayer from '../../components/album/StoryPlayer';
import AlbumStoryBuilder from '../../components/album/AlbumStoryBuilder';
import storyService from '../../services/storyService';
import storyReactionService from '../../services/storyReactionService';

import { StorySidebar } from '../../components/album/StorySidebar';
import { StoryCarousel } from '../../components/album/StoryCarousel';
import { ContentCreationModal } from '../album/modals/ContentCreationModal';

import { getHeaderConfig } from '../configs/headerConfigs';
import { SharedHeader } from '../shared/SharedHeader';
import { MediaExpressionsModal } from '../album/MediaExpressionsModal';
import { FileUpload } from '../common/FileUpload';
import { AlbumManager } from '../events/EventManager';

import { GalleryManager } from '../album/GalleryManager';
import { MediaUploadModal } from '../album/MediaUploadModal';

import { AlbumVoicePlayer } from '../album/voice/AlbumVoicePlayer';
import { GalleryVoiceBanner } from '../album/voice/GalleryVoiceBanner';
import { VoiceRecorder } from '../album/voice/VoiceRecorder';
import { GallerySidestrip } from '../album/GallerySidestrip';

const loadGoogleFont = (fontFamily) => {
  if (!fontFamily || fontFamily === 'Helvetica, sans-serif') return;
  
  const fontName = fontFamily.split(',')[0].trim().replace(/['"]/g, '');
  const linkId = `google-font-${fontName.replace(/\s+/g, '-')}`;
  
  if (document.getElementById(linkId)) return;
  
  const link = document.createElement('link');
  link.id = linkId;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${fontName.replace(/\s+/g, '+')}:wght@300;400;500;600;700&display=swap`;
  document.head.appendChild(link);
};


export const AlbumTemplate = ({ bookData, bookId, viewManager, customStyling = {} }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [galleries, setGalleries] = useState([]);
  const [activeGalleryId, setActiveGalleryId] = useState('all');
  const [galleryViewMode, setGalleryViewMode] = useState('masonry');
  const [sortBy, setSortBy] = useState('date');
  const [filterBy, setFilterBy] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedMedia, setSelectedMedia] = useState(null);
  const [lightboxIndex, setLightboxIndex] = useState(0);
  const [showShareModal, setShowShareModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [isOwner, setIsOwner] = useState(false);
  const [controlsOpen, setControlsOpen] = useState(false);
  const [editingMedia, setEditingMedia] = useState(null);

  const [showStories, setShowStories] = useState(false);
  const [stories, setStories] = useState([]);

  const [showStoryBuilder, setShowStoryBuilder] = useState(false);
  const [editingStory, setEditingStory] = useState(null);
  const [playingStory, setPlayingStory] = useState(null);

  const [selectedGalleryLayout, setSelectedGalleryLayout] = useState(null);
  const [galleryLayouts, setGalleryLayouts] = useState([]);

  const [sidebarStory, setSidebarStory] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const { viewMode, isAuthenticated, hasManagementAccess } = viewManager || { viewMode: 'public', isAuthenticated: false, hasManagementAccess: false };
  const [showAuthModal, setShowAuthModal] = useState(false);

  const [showContentCreationModal, setShowContentCreationModal] = useState(false);
  const [selectedMediaForContent, setSelectedMediaForContent] = useState(null);
  const [activeMediaModal, setActiveMediaModal] = useState({
    type: null, // 'expressions', 'transaction', 'fileUpload'
    mediaItem: null,
    galleryId: null,
    config: null
  });

  const [showAlbumVoice, setShowAlbumVoice] = useState(false);
  const [showVoiceRecorder, setShowVoiceRecorder] = useState(false);
  const [recordingContext, setRecordingContext] = useState(null);

  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadGalleryId, setUploadGalleryId] = useState(null);
  const [galleryUploadPermissions, setGalleryUploadPermissions] = useState({});
  const [canUpload, setCanUpload] = useState(false);
  const [isApprovedContributor, setIsApprovedContributor] = useState(false);
  const [approvedGalleries, setApprovedGalleries] = useState([]);
  const [sidestripCollapsed, setSidestripCollapsed] = useState(() => {
    return window.innerWidth <= 768;
  });

  const headerConfig = getHeaderConfig('album', {
    // Custom album-specific config
    showDescription: true
  });

  const getStripWidth = (width) => {
    const widthMap = {
      narrow: '64px',   
      medium: '80px',   
      wide: '96px'     
    };
    return widthMap[width] || widthMap.medium;
  };

  // Load galleries and media
  useEffect(() => {
    // Don't load galleries in management mode
    if (viewMode === 'management') {
      setLoading(false);
      return;
    }
    loadGalleries();
  }, [bookId, viewMode]);

  const loadGalleries = async () => {
    setLoading(true);
    
    try {
      const galleriesData = await albumService.getBookGalleries(bookId);
      
      // Filter out hidden media items for public view (non-management users)
      const filteredGalleries = galleriesData.map(gallery => ({
        ...gallery,
        mediaItems: 
           (gallery.mediaItems || []).filter(item => item.visible !== false) // Hide items where visible === false for public
      }));
    
      
      setGalleries(filteredGalleries);
      setIsOwner(bookData.ownerId === user?.uid);

      // Load stories
      const storiesData = await storyService.getAlbumStories(bookId);
      setStories(storiesData);
      
      if (filteredGalleries.length > 0 && activeGalleryId === 'all') {
        console.log('🔄 Changing activeGalleryId from "all" to:', filteredGalleries[0].id);
        setActiveGalleryId(filteredGalleries[0].id);
      }
    } catch (error) {
      console.error('❌ Error loading galleries:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (bookData?.voiceSettings?.enabled && 
        bookData?.voiceSettings?.albumNarration?.enabled && 
        bookData?.albumVoice?.isActive) {
      setShowAlbumVoice(true);
    }
  }, [bookData]);

  useEffect(() => {
    const fontFamily = customStyling?.fontFamily || bookData?.fontFamily || bookData?.styling?.fontFamily;
    
    if (fontFamily) {
      // Load Google Font
      loadGoogleFont(fontFamily);
      
      // Apply font globally
      const styleId = 'album-custom-font';
      let styleElement = document.getElementById(styleId);
      
      if (!styleElement) {
        styleElement = document.createElement('style');
        styleElement.id = styleId;
        document.head.appendChild(styleElement);
      }
      
      styleElement.textContent = `
        .album-container,
        .album-container * {
          font-family: ${fontFamily} !important;
        }
      `;
    }

    return () => {
      const styleElement = document.getElementById('album-custom-font');
      if (styleElement) {
        styleElement.remove();
      }
    };
  }, [customStyling?.fontFamily, bookData?.fontFamily, bookData?.styling?.fontFamily]);

  const checkIsOwner = () => {
    if (!user?.uid || !bookData) return false;
    
    return (
      bookData.ownerId === user.uid ||
      bookData.userId === user.uid ||
      bookData.creatorId === user.uid
    );
  };

  const isAuthorizedForVoiceRecording = () => {
    if (!user?.email || !bookData?.authorizedUsers) return false;
    
    const userAuth = bookData.authorizedUsers[user.email];
    return userAuth?.permissions?.voiceRecording === true;
  };

  const canUserRecord = (level = 'media', galleryId = null) => {
    // Check if voice features are enabled globally
    if (!bookData?.voiceSettings?.enabled) {
      return false;
    }

    let permissions;
    
    // Determine permissions based on level
    switch (level) {
      case 'album':
        // Album-level narration permissions
        if (!bookData.voiceSettings?.albumNarration?.enabled) {
          return false;
        }
        permissions = bookData.voiceSettings?.albumNarration?.permissions?.canRecord || 'owner-only';
        break;
        
      case 'gallery':
        // Gallery-level narration permissions
        if (!bookData.voiceSettings?.galleryNarration?.enabled) {
          return false;
        }
        
        // Check for gallery-specific permissions override
        if (galleryId) {
          const gallery = galleries.find(g => g.id === galleryId);
          permissions = gallery?.voicePermissions?.canRecord || 
                      bookData.voiceSettings?.galleryNarration?.permissions?.canRecord || 
                      'owner-only';
        } else {
          // Use default gallery permissions
          permissions = bookData.voiceSettings?.galleryNarration?.permissions?.canRecord || 'owner-only';
        }
        break;
        
      case 'media':
        // Media-level voice notes permissions
        if (!bookData.voiceSettings?.mediaVoiceNotes?.enabled) {
          return false;
        }
        permissions = bookData.voiceSettings?.mediaVoiceNotes?.permissions?.canRecord || 'authenticated';
        break;
        
      default:
        console.warn(`Unknown voice recording level: ${level}`);
        return false;
    }
    
    // Use the robust owner check
    const userIsOwner = checkIsOwner();
    
    // Check user permissions based on permission level
    switch (permissions) {
      case 'owner-only':
        return userIsOwner;
        
      case 'authorized-users':
        // Owner always has access, OR user must be authorized with voiceRecording permission
        return userIsOwner || isAuthorizedForVoiceRecording();
        
      case 'authenticated':
        return !!user;
        
      case 'anyone':
        return true;
        
      default:
        return userIsOwner;
    }
  };

  const handleMediaRecordVoice = (mediaItem, galleryId) => {
    setRecordingContext({
      level: 'media',
      data: {
        mediaItem,
        galleryId
      }
    });
    setShowVoiceRecorder(true);
  };

  const handleSaveVoiceNote = async (voiceData) => {
    try {
      const { audioBlob, duration, speaker, transcript } = voiceData;
      
      if (recordingContext.level === 'album') {
        await albumService.uploadAlbumVoice(bookId, audioBlob, {
          speaker,
          transcript,
          duration,
          language: 'en'
        });
        
      } else if (recordingContext.level === 'gallery') {
        await albumService.uploadGalleryVoice(
          bookId,
          recordingContext.data.galleryId,
          audioBlob,
          {
            speaker,
            transcript,
            duration,
            displayMode: bookData.voiceSettings?.galleryNarration?.displayMode || 'banner'
          }
        );
        
        // Reload galleries
        await loadGalleries();
        
      } else if (recordingContext.level === 'media') {
        // 🆕 HANDLE MEDIA VOICE NOTE
        const { mediaItem, galleryId } = recordingContext.data;
        
        await albumService.addMediaVoiceNote(
          bookId,
          galleryId,
          mediaItem.id,
          audioBlob,
          {
            speaker,
            transcript,
            duration,
            isPinned: speaker.isPinned || false,
            language: 'en',
            userId: user?.uid
          }
        );
        
        // Reload galleries to refresh media items with new voice notes
        await loadGalleries();
      }
      
      setShowVoiceRecorder(false);
      setRecordingContext(null);
      
      // Show success message
      console.log('✅ Voice note saved successfully!');
      
    } catch (error) {
      console.error('Error saving voice note:', error);
      alert('Failed to save voice note. Please try again.');
    }
  };

  const handleRecordGalleryVoice = (galleryId) => {
    if (!canUserRecord('gallery', galleryId)) {
      alert('You do not have permission to record gallery narration');
      return;
    }
    
    setRecordingContext({
      level: 'gallery',
      data: { galleryId }
    });
    setShowVoiceRecorder(true);
  };

  const handleStoryCreate = () => {
    setEditingStory(null);
    setShowStoryBuilder(true);
  };

  const handleStoryEdit = (story) => {
    setEditingStory(story);
    setShowStoryBuilder(true);
  };

  const handleStoryPlay = (story) => {
    if (story.displayMode === 'sidebar') {
      setSidebarStory(story);
      setSidebarOpen(true);
    } else {
      setPlayingStory(story);
    }
  };

  const handleFullScreenStoryPlay = (story, options = {}) => {
    setPlayingStory(story);
    // Pass options to story player if needed
    if (options.startSlide !== undefined) {
      // You might need to enhance StoryPlayer to accept starting slide
    }
  };

  const handleStoryDelete = async (storyId) => {
    if (!window.confirm('Are you sure you want to delete this story?')) return;
    
    try {
      await storyService.deleteStory(bookId, storyId);
      await loadGalleries(); // Reload to update stories
    } catch (error) {
      console.error('Error deleting story:', error);
      alert('Failed to delete story');
    }
  };

  // Group stories by display mode
  const storiesByDisplayMode = useMemo(() => {
    return {
      cards: stories.filter(s => s.displayMode === 'cards' || !s.displayMode),
      carousel: stories.filter(s => s.displayMode === 'carousel'),
      sidebar: stories.filter(s => s.displayMode === 'sidebar')
    };
  }, [stories]);

  const handleStorySave = async () => {
    setShowStoryBuilder(false);
    setEditingStory(null);
    await loadGalleries(); // Reload both galleries and stories
  };

  const handleStoryReaction = async (storyId, mediaId, reactionType, additionalData = {}) => {
    if (!user) {
      alert('Please sign in to react to stories');
      return;
    }

    try {
      let result;
      
      switch (reactionType) {
        case 'like':
          result = await storyReactionService.toggleLikeReaction(bookId, storyId, mediaId, user.uid);
          break;
          
        case 'comment':
          if (!additionalData.comment?.trim()) {
            alert('Please enter a comment');
            return;
          }
          result = await storyReactionService.addComment(bookId, storyId, mediaId, additionalData.comment, user.uid);
          break;
          
        case 'audio':
          if (!additionalData.audioFile) {
            alert('Audio file required');
            return;
          }
          result = await storyReactionService.addAudioReaction(bookId, storyId, mediaId, additionalData.audioFile, user.uid);
          break;
          
        case 'photo':
          if (!additionalData.photoFile) {
            alert('Photo file required');
            return;
          }
          result = await storyReactionService.addPhotoReaction(bookId, storyId, mediaId, additionalData.photoFile, user.uid);
          break;
          
        default:
          console.warn('💖 Unknown reaction type:', reactionType);
          return;
      }
      
      // Reload stories to update reaction counts
      await loadGalleries();
      
      // Show success message (optional)
      if (result && !result.removed) {
      } else if (result?.removed) {
      }
      
    } catch (error) {
      console.error('💖 Error handling story reaction:', error);
      alert('Failed to add reaction. Please try again.');
    }
  };

  // Filter and sort media items
  const processedMediaItems = useMemo(() => {

    let items = [];

    const storyItems = stories.map(story => {
      return { ...story, itemType: 'story' };
    });
    items = [...storyItems];

    // Then add media items from galleries
    if (activeGalleryId === 'all') {
      galleries.forEach(gallery => {
        if (gallery.mediaItems) {
          console.log(`📁 Gallery ${gallery.id} has ${gallery.mediaItems.length} media items`);
          const mediaItems = gallery.mediaItems.map(item => ({ ...item, itemType: 'media' }));
          items = [...items, ...mediaItems];
        }
      });
    } else {
      const activeGallery = galleries.find(g => g.id === activeGalleryId);
      if (activeGallery?.mediaItems) {
        const mediaItems = activeGallery.mediaItems.map(item => ({ ...item, itemType: 'media' }));
        items = [...items, ...mediaItems];
      }
    }

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      items = items.filter(item => {
        if (item.itemType === 'story') {
          return item.title?.toLowerCase().includes(query) ||
                item.description?.toLowerCase().includes(query);
        } else {
          // Search in multiple metadata locations
          const title = item.title || item.metadata?.title || '';
          const description = item.description || item.metadata?.description || '';
          const fileName = item.fileName || item.metadata?.fileName || '';
          const tags = item.tags || item.metadata?.tags || [];
          
          // Convert tags array to searchable string
          const tagsString = Array.isArray(tags) ? tags.join(' ') : '';
          
          return title.toLowerCase().includes(query) ||
                description.toLowerCase().includes(query) ||
                fileName.toLowerCase().includes(query) ||
                tagsString.toLowerCase().includes(query);
        }
      });
      console.log(`🔍 Search "${searchQuery}": ${items.length} results found`);
    }

    // Apply media type filter (stories are always included)
    if (filterBy !== 'all') {
      const beforeFilter = items.length;
      items = items.filter(item => {
        if (item.itemType === 'story') return true; // Always show stories
        
        switch (filterBy) {
          case 'images': return item.type === 'image';
          case 'videos': return item.type === 'video';
          case 'favorites': return item.isFavorited;
          default: return true;
        }
      });
    }

    // Apply sorting
    items.sort((a, b) => {
      // Always prioritize stories at the top
      if (a.itemType === 'story' && b.itemType !== 'story') return -1;
      if (b.itemType === 'story' && a.itemType !== 'story') return 1;
      
      // Sort within each type
      switch (sortBy) {
        case 'name':
          return (a.title || '').localeCompare(b.title || '');
        case 'popularity':
          return (b.interactions?.likes || 0) - (a.interactions?.likes || 0);
        case 'custom':
          return (a.order || 0) - (b.order || 0);
        case 'date':
        default:
          const aDate = a.itemType === 'story' ? a.createdAt : a.uploadedAt;
          const bDate = b.itemType === 'story' ? b.createdAt : b.uploadedAt;
          return new Date(bDate) - new Date(aDate);
      }
    });
    
    return items;
  }, [galleries, stories, activeGalleryId, searchQuery, filterBy, sortBy]);

  const handleMediaClick = (mediaItem, clickedIndex) => {
    // Don't open lightbox for story items
    if (mediaItem?.itemType === 'story') {
      return;
    }

    console.log('🖱️ Media clicked:', { 
      id: mediaItem.id, 
      galleryId: mediaItem.galleryId,
      clickedIndex 
    });

    // ✅ FIX: Find the correct index in processedMediaItems
  
    const mediaOnlyItems = processedMediaItems.filter(item => item.itemType === 'media');
    
    const correctIndex = mediaOnlyItems.findIndex(
      item => item.id === mediaItem.id && item.galleryId === mediaItem.galleryId
    );

    console.log('✅ Found index in processedMediaItems:', correctIndex);

    // Safety check: if item not found, log details and exit
    if (correctIndex === -1) {
      console.error('❌ Media item not found in processed items:', {
        searchingFor: { id: mediaItem.id, galleryId: mediaItem.galleryId },
        mediaItem,
        totalProcessedItems: processedMediaItems.length,
        mediaOnlyItems: mediaOnlyItems.length,
        availableIds: mediaOnlyItems.map(m => ({ id: m.id, galleryId: m.galleryId }))
      });
      return;
    }

    setSelectedMedia(mediaItem);
    setLightboxIndex(correctIndex);
  };

  const handleCardUpdate = (action, data) => {
    if (action === 'openContentModal') {
      setSelectedMediaForContent(data);
      setShowContentCreationModal(true);
    }
    // Handle other card actions
  };

  const columnsCountBreakPoints = useMemo(() => {
    const albumSettings = bookData?.albumSettings;
    const breakpoints = albumSettings?.columnsBreakpoints;
    
    if (!breakpoints) {
      // Fallback to default breakpoints
      return { 350: 1, 750: 2, 900: 3, 1200: 4 };
    }

    // Map semantic breakpoint names to pixel values
    const breakpointMapping = {
      mobile: 350,    // Mobile devices
      tablet: 750,    // Tablet devices
      desktop: 900,   // Desktop devices
      wide: 1200      // Wide screen devices
    };

    const dynamicBreakpoints = {};
    
    // Convert semantic breakpoints to pixel-based breakpoints
    Object.entries(breakpointMapping).forEach(([semantic, pixels]) => {
      if (breakpoints[semantic] !== undefined) {
        dynamicBreakpoints[pixels] = breakpoints[semantic];
      }
    });

    // Ensure we always have at least a mobile breakpoint
    if (Object.keys(dynamicBreakpoints).length === 0) {
      return { 350: 1, 750: 2, 900: 3, 1200: 4 };
    }

    // Sort breakpoints by pixel value to ensure proper responsive behavior
    const sortedBreakpoints = {};
    Object.keys(dynamicBreakpoints)
      .map(Number)
      .sort((a, b) => a - b)
      .forEach(pixel => {
        sortedBreakpoints[pixel] = dynamicBreakpoints[pixel];
      });

    return sortedBreakpoints;
  }, [bookData?.albumSettings?.columnsBreakPoints]);

  const handleOpenMediaModal = useCallback((type, mediaItem, galleryId, config = null) => {
    setActiveMediaModal({
      type,
      mediaItem,
      galleryId,
      config
    });
  }, []);

  const handleCloseMediaModal = useCallback(() => {
    setActiveMediaModal({
      type: null,
      mediaItem: null,
      galleryId: null,
      config: null
    });
  }, []);

  // ADD useEffect to check permissions
  useEffect(() => {
    if (viewMode !== 'management') {
      checkUploadPermissions();
    }
  }, [user, bookData, bookId, viewMode]);

  const handleRecordAlbumVoice = () => {
    if (!canUserRecord('album')) {
      alert('You do not have permission to record album narration');
      return;
    }
    
    setRecordingContext({
      level: 'album',
      data: null
    });
    setShowVoiceRecorder(true);
  };

  const handleOpenExpressionsFromLightbox = (mediaItem, galleryId) => {
    // Close lightbox
    setSelectedMedia(null);
    setLightboxIndex(0);
    
    // Open expressions modal
    setActiveMediaModal({
      type: 'expressions',
      mediaItem: mediaItem,
      galleryId: galleryId || activeGalleryId
    });
  };

  const handleOpenVoiceRecorderFromLightbox = (context) => {
    // Close lightbox
    setSelectedMedia(null);
    setLightboxIndex(0);
    
    // Open voice recorder
    setRecordingContext(context);
      setShowVoiceRecorder(true);
    };

    const handleContentCreate = (mediaItem, galleryId) => {
      setSelectedMediaForContent({ mediaItem, galleryId });
      setShowContentCreationModal(true);
    };

  // Handler to close and refresh after content creation
  const handleContentCreated = async (newContent) => {
    setShowContentCreationModal(false);
    setSelectedMediaForContent(null);
    
    // Refresh the gallery data to show the new content
    if (activeGalleryId && activeGalleryId !== 'all') {
      try {
        const updatedGallery = await albumService.getGallery(bookId, activeGalleryId);
        setGalleries(prev => 
          prev.map(g => g.id === activeGalleryId ? updatedGallery : g)
        );
      } catch (error) {
        console.error('Error refreshing gallery:', error);
      }
    }
  };

  const checkUploadPermissions = async (galleryId = null) => {
    if (!user || !bookData) return { canUpload: false, galleryPermissions: {} };

    // Owner can always upload
    if (isOwner) {
      setCanUpload(true);
      setIsApprovedContributor(true);
      return { canUpload: true, galleryPermissions: {} };
    }

    // Check gallery-specific permissions if galleryId provided
    if (galleryId) {
      const gallery = galleries.find(g => g.id === galleryId);
      const galleryPerms = gallery?.categoryConfig?.galleryUploadPermissions;

      if (galleryPerms?.enabled) {
        // Public mode - any authenticated user can upload
        if (galleryPerms.mode === 'public') {
          return { 
            canUpload: true, 
            galleryPermissions: galleryPerms,
            requiresModeration: galleryPerms.requireModeration,
            canEditOwn: galleryPerms.allowEditOwn
          };
        }

        // Authorized mode - check if user is in the list
        if (galleryPerms.mode === 'authorized') {
          const isAuthorized = galleryPerms.authorizedGalleryUsers?.some(
            u => u.email === user.email.toLowerCase()
          );
          return { 
            canUpload: isAuthorized, 
            galleryPermissions: galleryPerms,
            requiresModeration: galleryPerms.requireModeration,
            canEditOwn: galleryPerms.allowEditOwn
          };
        }

        return { canUpload: false, galleryPermissions: galleryPerms };
      }
    }

    // Fall back to album-level permissions
    if (!bookData.uploadSettings?.allowUserUploads) {
      setCanUpload(false);
      return { canUpload: false, galleryPermissions: {} };
    }

    if (bookData.uploadSettings.userUploadMode === 'authenticated') {
      setCanUpload(true);
      setIsApprovedContributor(false);
      return { canUpload: true, galleryPermissions: {} };
    } else if (bookData.uploadSettings.userUploadMode === 'approved') {
      const isApproved = await albumService.isApprovedContributor(bookId, user.uid);
      setCanUpload(isApproved);
      setIsApprovedContributor(isApproved);
      return { canUpload: isApproved, galleryPermissions: {} };
    }

    return { canUpload: false, galleryPermissions: {} };
  };

  const handleOpenUploadModal = async (galleryId) => {
    const permissions = await checkUploadPermissions(galleryId);
    
    if (!permissions.canUpload) {
      alert('You do not have permission to upload to this gallery');
      return;
    }

    setUploadGalleryId(galleryId);
    setShowUploadModal(true);
  };

  const handleUploadComplete = () => {
    setShowUploadModal(false);
    loadGalleries();
  };

  const isVoiceEnabled = bookData?.voiceSettings?.enabled === true;
  const isAlbumVoiceEnabled = isVoiceEnabled && bookData?.voiceSettings?.albumNarration?.enabled === true;
  const isGalleryVoiceEnabled = isVoiceEnabled && bookData?.voiceSettings?.galleryNarration?.enabled === true;
  const isMediaVoiceEnabled = isVoiceEnabled && bookData?.voiceSettings?.mediaVoiceNotes?.enabled === true;

  const renderGalleryWithLayout = (gallery) => {
    const { layoutConfig, mediaItems = [] } = gallery;
    
    // Get stories for this gallery based on level and galleryId
    const galleryStories = stories.filter(story => {
      if (story.level === 'gallery') {
        return story.galleryId === gallery.id;
      }
      // For album-level stories, only include them in the first gallery when viewing "all"
      if (story.level === 'album' && activeGalleryId === 'all') {
        const firstGallery = galleries[0];
        return gallery.id === firstGallery?.id;
      }
      return false;
    });

    // Group stories by display mode
    const storiesByMode = {
      cards: galleryStories.filter(s => s.displayMode === 'cards' || !s.displayMode),
      carousel: galleryStories.filter(s => s.displayMode === 'carousel'),
      sidebar: galleryStories.filter(s => s.displayMode === 'sidebar')
    };

    // Combine card stories with media items for layout rendering
    const combinedItems = [
      ...storiesByMode.cards.map(story => ({ ...story, itemType: 'story' })),
      ...mediaItems.map(item => ({ ...item, itemType: 'media' }))
    ];

    // Early return for empty gallery (no media AND no stories)
    if (combinedItems.length === 0 && storiesByMode.carousel.length === 0) {
      return (
        <div className="text-center py-12 border-2 border-dashed border-gray-300 rounded-lg">
          <ImageIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-sm font-medium text-gray-900 mb-2">No content in this gallery</h3>
          <p className="text-sm text-gray-500">This gallery is empty.</p>
          {isOwner && (
            <div className="mt-4 space-x-3">
              <button
                onClick={handleStoryCreate}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Create Story
              </button>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className="gallery-with-stories">
        {/* Carousel Stories at Top */}
        {storiesByMode.carousel.length > 0 && (
          <div className="mb-4">
            <StoryCarousel
              stories={storiesByMode.carousel}
              mediaItems={mediaItems}
              onStoryPlay={handleStoryPlay}
              onStoryEdit={handleStoryEdit}
              onStoryDelete={handleStoryDelete}
              isOwner={isOwner}
              primaryColor={customStyling.primaryColor}
            />
          </div>
        )}

        {/* Sidebar Stories Trigger */}
        {storiesByMode.sidebar.length > 0 && (
          <div className="fixed right-4 top-1/2 transform -translate-y-1/2 z-30">
            <div className="relative">
              <button
                onClick={() => {
                  setSidebarStory(storiesByMode.sidebar[0]);
                  setSidebarOpen(true);
                }}
                className="bg-purple-600 text-white p-3 rounded-full shadow-lg hover:bg-purple-700 transition-colors group"
                title={`View ${storiesByMode.sidebar.length} Stories`}
              >
                <BookOpen className="h-6 w-6" />
              </button>
              
              {/* Story count badge */}
              {storiesByMode.sidebar.length > 1 && (
                <div className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-6 h-6 flex items-center justify-center font-bold">
                  {storiesByMode.sidebar.length}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Main Gallery Content (Media + Card Stories) */}
        {combinedItems.length > 0 && (
          <>
            {/* If gallery has layout configuration, use LayoutRenderer */}
            {layoutConfig && layoutConfig.category && layoutConfig.layout ? (
              <div className="gallery-layout-content">
                <LayoutRenderer
                  user={user}
                  layoutConfig={layoutConfig}
                  mediaItems={combinedItems}  // ✅ Combined stories + media
                  galleryId={gallery.id}
                  bookId={bookId}
                  bookData={bookData}
                  albumSettings={bookData.albumSettings}
                  isOwner={isOwner}
                  customStyling={customStyling}
                  gallery={gallery}
                  onContentCreate={handleContentCreate}
                  responsiveBreakpoints={columnsCountBreakPoints}
                  onOpenModal={handleOpenMediaModal}
                  onClick={(item) => handleMediaClick(item)}
                  setSelectedMedia={setSelectedMedia}
                  onStoryPlay={handleStoryPlay}
                  onStoryEdit={handleStoryEdit}
                  onRecordVoice={handleMediaRecordVoice}
                  currentUserId={user?.uid}
                />
              </div>
            ) : (
              /* Fallback to default masonry if no layout config */
              <div className="gallery-masonry-content">
                <ResponsiveMasonry columnsCountBreakPoints={columnsCountBreakPoints}>
                  <Masonry gutter="16px">
                    {combinedItems.map((item, index) => (
                     
                        <div key={`media-${item.id}`} className="mb-4">
                          <MediaCard
                            key={`${item.galleryId}-${item.id}`}
                            mediaItem={item}
                            galleryId={item.galleryId}
                            bookId={bookId}
                            bookData={bookData}
                            albumSettings={bookData.albumSettings}
                            customStyling={customStyling}
                            onClick={() => handleMediaClick(item)}
                            onEdit={isOwner ? (() => setEditingMedia(item)) : undefined}
                            onOpenModal={handleOpenMediaModal} // New prop
                            user={user}
                            index={index}
                            isOwner={isOwner}
                            isEditing={editingMedia?.id === item.id}
                            onRecordVoice={handleMediaRecordVoice}
                          />
                        </div>
                
                    ))}
                  </Masonry>
                </ResponsiveMasonry>
              </div>
            )}
          </>
        )}

        {/* Empty state for when there are carousel/sidebar stories but no card stories or media */}
        {combinedItems.length === 0 && (storiesByMode.carousel.length > 0 || storiesByMode.sidebar.length > 0) && (
          <div className="text-center py-12">
            <Grid className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Gallery Content</h3>
            <p className="text-gray-500 mb-4">
              This gallery has {storiesByMode.carousel.length + storiesByMode.sidebar.length} stories but no media items.
            </p>
            {isOwner && (
              <button
                onClick={() => {/* Navigate to upload */}}
                className="inline-flex items-center gap-2 px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
              >
                <Plus className="h-4 w-4" />
                Add Photos
              </button>
            )}
          </div>
        )}
      </div>
    );
  };

  const Header = ({ isOwner, bookData, customStyling, navigate }) => {
    const [isSquare, setIsSquare] = useState(true);

    const handleImageLoad = (e) => {
      const { naturalWidth, naturalHeight } = e.target;
      setIsSquare(naturalWidth === naturalHeight);
    };

    return (
      <div className="flex items-center gap-2">
        {/* Return Arrow for Album/Book Owner */}
        {isOwner && (
          <button
            onClick={() => navigate("/dashboard")}
            className="p-0 hover:bg-gray-100 rounded-lg transition-colors"
            title="Return to Dashboard"
          >
            <ArrowLeft className="h-5 w-5 text-gray-600" />
          </button>
        )}

        {/* Logo Display */}
        {bookData?.logoUrl && (
          <div className="flex-shrink-0">
            <img
              src={bookData.logoUrl}
              alt={`${bookData.title || bookData.botName} logo`}
              className="h-10 w-auto object-contain rounded-lg"
              onLoad={handleImageLoad}
            />
          </div>
        )}

        {/* Title Section (only show if logo is square OR no logo at all) */}
        {(isSquare || !bookData?.logoUrl) && (
          <div className="min-w-0">
            <h1
              className="text-2xl font-bold text-gray-900 truncate"
              style={{ color: customStyling.primaryColor }}
            >
              {bookData.title || bookData.botName}
            </h1>
            {bookData.description && (
              <p className="text-gray-600 mt-1 text-sm line-clamp-2">
                {bookData.description}
              </p>
            )}
          </div>
        )}
      </div>
    );
  }  

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-pink-500"></div>
      </div>
    );
  }

  if (viewMode === 'management' && hasManagementAccess) {
    return (
      <>
        <GalleryManager
          user={user}
          bookId={bookId}
          bookData={bookData}
          isOpen={true}
          onClose={() => {
            // Switch back to public view when closing
            if (viewManager?.toggleView) {
              viewManager.toggleView();
            }
          }}
          onUpdate={loadGalleries}
          customStyling={customStyling}
        />
        
      </>
    );
  } 

  return (
    <div className="album-container min-h-screen bg-gray-50" style={{ 
      backgroundColor: customStyling.backgroundColor
    }}>
      {/* Header */}
      <SharedHeader
        templateType="album"
        bookData={bookData}
        styling={customStyling}
        isOwner={isOwner}
        navigate={navigate}
        state={{
          searchQuery,
          showFilters: controlsOpen,
          activeGalleryId,
          galleries,
          viewMode: galleryViewMode,
          filterBy,
          sortBy
        }}
        actions={{
          onSearchChange: setSearchQuery,
          onToggleFilters: () => setControlsOpen(v => !v),
          onGalleryChange: setActiveGalleryId,
          onViewModeChange: setGalleryViewMode,
          onFilterChange: setFilterBy,
          onSortChange: setSortBy,
          onShowShare: () => setShowShareModal(true),
          onCreateStory: handleStoryCreate,
          onRecordAlbumVoice: handleRecordAlbumVoice,
        }}
      />

      {/* Gallery Sidestrip Navigation */}
      {bookData?.albumSettings?.sidestrip?.enabled && galleries.length > 0 && (
        <GallerySidestrip
          bookData={bookData}
          galleries={galleries}
          activeGalleryId={activeGalleryId}
          onGalleryChange={setActiveGalleryId}
          settings={bookData.albumSettings.sidestrip}
          customStyling={customStyling}
          headerHeight={bookData?.albumSettings?.headerSettings?.headerHeight || 80}
          isCollapsed={sidestripCollapsed}
          onToggleCollapse={() => setSidestripCollapsed(!sidestripCollapsed)}
        />
      )}

      {/* Main Content */}
    <div 
        className="transition-all duration-300"
        style={{
          marginLeft: bookData?.albumSettings?.sidestrip?.enabled && 
                      galleries.length > 0 && 
                      !sidestripCollapsed && 
                      bookData.albumSettings.sidestrip.position === 'left'
            ? getStripWidth(bookData.albumSettings.sidestrip.width)
            : '0',
          marginRight: bookData?.albumSettings?.sidestrip?.enabled && 
                      galleries.length > 0 && 
                      !sidestripCollapsed && 
                      bookData.albumSettings.sidestrip.position === 'right'
            ? getStripWidth(bookData.albumSettings.sidestrip.width)
            : '0'
        }}
    >
      <div className="max-w-8xl mx-auto px-4 sm:px-2 lg:px-8 py-4">
        {loading ? (
          <div className="text-center py-8">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
            <p className="text-gray-500 mt-4">Loading galleries...</p>
          </div>
        ) : activeGalleryId === 'all' ? (
          // Render all galleries with their individual layouts
          <div className="space-y-12">
            {/* Stories Section - Album level stories (if any) 
            {stories.filter(s => s.level === 'album').length > 0 && (
              <div className="album-stories-section">
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                    <BookOpen className="h-6 w-6 text-purple-600" />
                    Album Stories
                  </h2>
                  <span className="text-sm text-gray-500">
                    {stories.filter(s => s.level === 'album').length} stories
                  </span>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {stories.filter(s => s.level === 'album').map(story => (
                    <StoryCard
                      key={`album-story-${story.id}`}
                      story={story}
                      mediaItems={processedMediaItems.filter(i => i.itemType === 'media')}
                      onClick={handleStoryPlay}
                      isOwner={isOwner}
                      primaryColor={customStyling.primaryColor}
                    />
                  ))}
                </div>
              </div>
            )}
            */}
            {/* All Galleries Section */}
            {galleries.length > 0 ? (
              galleries.map((gallery, index) => (
                <div key={gallery.id} className="gallery-section"
                style={{ color: customStyling.textColor }}
                >
                  {/* Gallery Header */}
                  <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                      <h2 className="text-2xl font-semibold">
                        {gallery.name}</h2>
                      
                      {gallery.layoutConfig && (
                        <span className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm font-medium">
                          {gallery.layoutConfig.layout || 'masonry'}
                        </span>
                      )}
                      {/* Gallery Stories Indicator */}
                      {stories.filter(s => s.level === 'gallery' && s.galleryId === gallery.id).length > 0 && (
                        <span className="hidden sm:inline px-3 py-1 bg-purple-500 text-white rounded-full text-sm font-medium">
                          {stories.filter(s => s.level === 'gallery' && s.galleryId === gallery.id).length} stories
                        </span>
                      )}
                    </div>
      
                    <div className="flex items-center justify-end gap-3">
                      <div className="text-sm">
                        {gallery.mediaItems?.length || 0} items
                      </div>
                      
                      {/* UPLOAD BUTTON - Shows for authorized users */}
                      {(canUpload || gallery?.categoryConfig?.galleryUploadPermissions?.enabled) && (
                      <button
                        onClick={() => handleOpenUploadModal(gallery.id)}
                        className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                        style={{ background: customStyling.primaryColor }}
                      >
                        <Upload className="h-4 w-4" />
                      </button>
                    )}
                    </div>
                  </div>

                  {/* GALLERY VOICE BANNER */}
                  {isGalleryVoiceEnabled && (
                    <GalleryVoiceBanner
                      gallery={gallery}
                      voiceSettings={bookData.voiceSettings}
                      onSegmentPlay={(segment) => {
                        // Handle segment playback
                        console.log('Playing segment:', segment);
                      }}
                    />
                  )}
                
                  
                  {/* Render gallery with its configured layout */}
                  {renderGalleryWithLayout(gallery)}
                </div>
              ))
            ) : (
              <div className="text-center py-8">
                <div className="max-w-md mx-auto">
                  <div className="bg-gray-100 rounded-full p-6 w-24 h-24 mx-auto mb-6">
                    <Grid className="h-12 w-12 text-gray-400 mx-auto" />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">No galleries found</h3>
                  <p className="text-gray-500">Create some galleries to get started</p>
                  {isOwner && (
                    <p className="text-gray-400 mt-2 text-sm">
                      Go to the editor to add galleries and photos
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        ) : (
          // Render single selected gallery
          (() => {
            const currentGallery = galleries.find(g => g.id === activeGalleryId);
            const galleryStories = stories.filter(s => s.level === 'gallery' && s.galleryId === activeGalleryId);
            
            if (!currentGallery) {
              return (
                <div className="text-center py-8">
                  <div className="max-w-md mx-auto">
                    <div className="bg-gray-100 rounded-full p-6 w-24 h-24 mx-auto mb-6">
                      <Grid className="h-12 w-12 text-gray-400 mx-auto" />
                    </div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Gallery not found</h3>
                    <p className="text-gray-500">The selected gallery could not be loaded</p>
                  </div>
                </div>
              );
            }
            
            return (
              <div className="single-gallery">
                {/* Gallery Info Header */}
                <div 
                  className="flex items-center justify-between py-4"
                  style={{ color: customStyling.textColor }}
                >
                  <div className="flex items-center justify-start gap-3">
                    <h2 className="text-md md:text-xl font-semibold">{currentGallery.name}</h2>
                    
                    {currentGallery.layoutConfig && (
                      <span className="px-3 py-1 bg-blue-100 rounded-full text-sm border font-medium"
                      style={{ backgroundColor: customStyling.headerColor,
                               color: customStyling.accentColor,
                       }}
                      >
                        {currentGallery.layoutConfig.layout || 'masonry'}
                      </span>
                    )}

                    {galleryStories.length > 0 && (
                      <span className="hidden sm:inline px-3 py-1 bg-purple-600 rounded-full text-sm font-medium">
                        {galleryStories.length} stories
                      </span>
                    )}
                  </div>

                  <div className="flex items-center justify-end gap-3">
                    <div className="text-sm">
                      {currentGallery.mediaItems?.length || 0} items
                    </div>

                    {(canUpload || currentGallery?.categoryConfig?.galleryUploadPermissions?.enabled) && (
                      <button
                        onClick={() => handleOpenUploadModal(currentGallery.id)}
                        className="flex items-center gap-2 px-4 py-2 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm"
                        style={{ background: customStyling.primaryColor }}
                      >
                        <Upload className="h-4 w-4" />
                      </button>
                    )}
                  </div>
                </div>

                {/* Gallery Stories Section (if any) 
                {galleryStories.length > 0 && (
                  <div className="gallery-stories-section mb-8">
                    <h3 className="text-lg font-semibold text-gray-900 mb-4 flex items-center gap-2">
                      <BookOpen className="h-5 w-5 text-purple-600" />
                      Gallery Stories
                    </h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                      {galleryStories.map(story => (
                        <div 
                          key={`gallery-story-${story.id}`}
                          className="border-l-4 border-purple-500 pl-4"
                        >
                          <StoryCard
                            story={story}
                            mediaItems={currentGallery.mediaItems || []}
                            onClick={handleStoryPlay}
                            isOwner={isOwner}
                            primaryColor={customStyling.primaryColor}
                            compact={true}
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                */}

                {/* GALLERY VOICE BANNER */}
                  {isGalleryVoiceEnabled && (
                  <GalleryVoiceBanner
                    gallery={currentGallery}
                    voiceSettings={bookData.voiceSettings}
                    onSegmentPlay={(segment) => {
                      console.log('Playing segment:', segment);
                    }}
                  />
                )}
                {/* Render the gallery with its configured layout */}
                {renderGalleryWithLayout(currentGallery)}
              </div>
            );
          })()
        )}
      </div>
    </div>

      {showUploadModal && uploadGalleryId && (
        <MediaUploadModal
          bookId={bookId}
          bookData={bookData}
          customStyling={customStyling}
          user={user}
          galleryId={uploadGalleryId}
          isApprovedContributor={isApprovedContributor}
          onClose={() => setShowUploadModal(false)}
          onUploadComplete={handleUploadComplete}
        />
      )}

      {selectedMedia && (
        <MediaLightbox
          bookData={bookData}
          bookId={bookId}  // ADD THIS
          media={processedMediaItems.filter(item => item.itemType === 'media')}
          currentIndex={lightboxIndex}
          onClose={() => {
            setSelectedMedia(null);
            setLightboxIndex(0);
          }}
          onNavigate={(newIndex) => {  
            setLightboxIndex(newIndex);
          }}
          onOpenExpressions={handleOpenExpressionsFromLightbox}  
          onOpenVoiceRecorder={handleOpenVoiceRecorderFromLightbox}  
        />
      )}

      {/* Share Modal */}
      <ShareModal 
          content={bookData}
          contentType="album" 
          isOpen={showShareModal} 
          onClose={() => setShowShareModal(false)}
      />

      {/* Story Builder Modal */}
      {showStoryBuilder && (
        <AlbumStoryBuilder
          user={user}
          albumId={bookId}
          galleries={galleries}
          mediaItems={processedMediaItems.filter(item => item.itemType === 'media')}
          existingStory={editingStory}
          onSave={handleStorySave}
          onClose={() => setShowStoryBuilder(false)}
        />
      )}

      {/* Story Player */}
      {playingStory && (
        <StoryPlayer
          story={playingStory}
          mediaItems={processedMediaItems.filter(item => item.itemType === 'media')}
          albumId={bookId}
          onClose={() => setPlayingStory(null)}
          onReaction={handleStoryReaction}
          autoPlay={true}
        />
        )}

      {sidebarStory && (
        <StorySidebar
          story={sidebarStory}
          mediaItems={processedMediaItems.filter(i => i.itemType === 'media')}
          isOpen={sidebarOpen}
          onClose={() => {
            setSidebarOpen(false);
            setSidebarStory(null);
          }}
          onFullScreenPlay={handleFullScreenStoryPlay}  // NEW: Pass the handler
          position={sidebarStory?.sidebarPosition || 'right'}
          width={sidebarStory?.sidebarWidth || 'normal'}
        />
      )}

      {showContentCreationModal && selectedMediaForContent && (
        <ContentCreationModal
          mediaItem={selectedMediaForContent.mediaItem}
          galleryId={selectedMediaForContent.galleryId}
          bookId={bookId}
          onContentCreate={handleContentCreate}
          primaryColor={customStyling.primaryColor}
          onClose={() => {
            setShowContentCreationModal(false);
            setSelectedMediaForContent(null);
          }}
        />
      )}

      {/* Media Expressions Modal */}
      {activeMediaModal.type === 'expressions' && activeMediaModal.mediaItem && (
        <MediaExpressionsModal
          mediaItem={activeMediaModal.mediaItem}
          bookId={bookId}
          bookData={bookData}
          galleryId={activeMediaModal.galleryId}
          isOpen={true}
          onClose={handleCloseMediaModal}
          primaryColor={customStyling.primaryColor}
        />
      )}

      {/* VOICE RECORDER MODAL */}
      {showVoiceRecorder && recordingContext && (
        <VoiceRecorder
          level={recordingContext.level}
          mediaItem={recordingContext.data?.mediaItem}
          onSave={handleSaveVoiceNote}
          onCancel={() => {
            setShowVoiceRecorder(false);
            setRecordingContext(null);
          }}
        />
      )}

      {/* Transaction Modal */}
      {activeMediaModal.type === 'transaction' && activeMediaModal.mediaItem && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
            {/* Header */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div 
                  className="w-3 h-3 rounded-full"
                  style={{ backgroundColor: activeMediaModal.config?.color }}
                />
                <h3 className="text-lg font-semibold text-gray-900">
                  {activeMediaModal.config?.title}
                </h3>
                {activeMediaModal.config?.description && (
                  <span className="text-sm text-gray-500">
                    • {activeMediaModal.config.description}
                  </span>
                )}
              </div>
              
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    window.open(activeMediaModal.config?.url, '_blank', 'noopener,noreferrer');
                    handleCloseMediaModal();
                  }}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                  title="Open in new tab"
                >
                  <ExternalLink className="h-4 w-4" />
                </button>
                <button
                  onClick={handleCloseMediaModal}
                  className="p-2 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* iframe Content */}
            <div className="flex-1 relative overflow-hidden">
              <iframe
                src={activeMediaModal.config?.url}
                className="w-full h-full border-none"
                title={`${activeMediaModal.config?.title} - ${activeMediaModal.type}`}
                sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation"
              />
            </div>
          </div>
        </div>
      )}

      {/* File Upload Modal */}
      {activeMediaModal.type === 'fileUpload' && activeMediaModal.mediaItem && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-md w-full">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold">Add Photo Response</h3>
              <button
                onClick={handleCloseMediaModal}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <FileUpload
              accept="image/*"
              maxFiles={1}
              maxSize={10 * 1024 * 1024} // 10MB
              onFilesSelect={(files) => {
                // Handle file upload logic here
                console.log('Files uploaded:', files);
                handleCloseMediaModal();
              }}
              className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center"
            />
          </div>
        </div>
      )}
    </div>
  );
};